import React, { useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  Button,
  Typography,
  useTheme
} from "@mui/material";
import { BCPTrophy, BCPMedal } from "../../../components/icons/bcp/BCPIcons";
import { FactionIcon } from "../../../components/icons/FactionIcon";
import StatBox from "./StatBox";

const typeIs = (header, type) => header?.type?.toLowerCase() === type;

const isRankHeader = (header) =>
  typeIs(header, "rank") || header?.label?.trim() === "#";

const isPercentageHeader = (header) =>
  typeIs(header, "metaaverage") ||
  typeIs(header, "percentage") ||
  /win\s*%|win\s*rate/i.test(header?.label || "");

function getColoredStat(value, type, isDark) {
  const colors = {
    wins: isDark ? "#4ade80" : "#16a34a",
    losses: isDark ? "#f87171" : "#dc2626",
    draws: isDark ? "#fbbf24" : "#d97706"
  };

  return (
    <Box component="span" sx={{ color: colors[type], fontWeight: 600 }}>
      {value}
    </Box>
  );
}

function RankCell({ header, value, position, isDark }) {
  if (!isRankHeader(header)) return value ?? "";

  const isUnranked = value === null || value === undefined;

  const asterisk = isUnranked ? (
    <Box
      component="span"
      sx={{
        color: "text.secondary",
        fontSize: "0.8rem",
        fontWeight: 700,
        ml: 0.3,
        opacity: 0.65,
        lineHeight: 1,
        verticalAlign: "super"
      }}
    >
      *
    </Box>
  ) : null;

  if (value === 1) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.5
        }}
      >
        <BCPTrophy size="sm" style={{ color: "#FFD700" }} />
        <Box sx={{ color: "#FFD700", fontWeight: 700, fontSize: "1rem" }}>
          {position}
        </Box>
      </Box>
    );
  }

  if (value === 2) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.5
        }}
      >
        <BCPMedal size="sm" style={{ color: "#C0C0C0" }} />
        <Box sx={{ color: "#C0C0C0", fontWeight: 700, fontSize: "1rem" }}>
          {position}
        </Box>
      </Box>
    );
  }

  if (value === 3) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.5
        }}
      >
        <BCPMedal size="sm" style={{ color: "#CD7F32" }} />
        <Box sx={{ color: "#CD7F32", fontWeight: 700, fontSize: "1rem" }}>
          {position}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <Box
        component="span"
        sx={{
          color: "text.secondary",
          fontWeight: 500,
          fontSize: "0.9rem"
        }}
      >
        {position}
        {asterisk}
      </Box>
    </Box>
  );
}

function IconObjectCell({ value, theme }) {
  if (!value) return null;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <FactionIcon
        icon={value.icon}
        name={value.name}
        size={32}
        sx={{ fontSize: "0.75rem", fontWeight: 600 }}
      />
      <Box sx={{ color: theme.palette.text.primary, fontWeight: 700 }}>
        {value.name}
      </Box>
    </Box>
  );
}

function WldCell({ value, isDark }) {
  if (!Array.isArray(value)) return null;
  const [wins, losses, draws] = value;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: 0.5
      }}
    >
      {getColoredStat(wins, "wins", isDark)}
      <Box component="span" sx={{ color: "text.secondary" }}>
        -
      </Box>
      {getColoredStat(losses, "losses", isDark)}
      <Box component="span" sx={{ color: "text.secondary" }}>
        -
      </Box>
      {getColoredStat(draws, "draws", isDark)}
    </Box>
  );
}

function PercentageCell({ value, color, theme }) {
  if (value === null || value === undefined) return null;
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end"
      }}
    >
      <Typography
        sx={{
          color,
          fontWeight: 700,
          fontSize: "0.875rem",
          lineHeight: 1.3,
          fontVariantNumeric: "tabular-nums"
        }}
      >
        {value}%
      </Typography>
      <Box
        sx={{
          width: 80,
          height: 5,
          borderRadius: 3,
          backgroundColor:
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.08)"
              : "rgba(0,0,0,0.08)",
          mt: 0.5,
          overflow: "hidden"
        }}
      >
        <Box
          sx={{
            height: "100%",
            width: `${value}%`,
            borderRadius: 3,
            background: `linear-gradient(90deg, ${color}55, ${color})`
          }}
        />
      </Box>
    </Box>
  );
}

function ActionsCell({
  actions,
  hasChildren,
  isExpanded,
  onToggleExpand,
  isDark
}) {
  if (!Array.isArray(actions) || actions.length === 0) return null;

  return (
    <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
      {actions.map((action, i) => {
        if (action?.type !== "Button") return null;

        if (action.buttonType === "ExpandChildren") {
          if (!hasChildren) return null;
          return (
            <Button
              key={`action-${i}`}
              size="small"
              variant="contained"
              onClick={onToggleExpand}
              sx={{
                minWidth: 70,
                height: 28,
                px: 1.5,
                py: 0.5,
                fontSize: "0.75rem",
                backgroundColor: isDark ? "#8b5cf6" : "#7c3aed",
                "&:hover": {
                  backgroundColor: isDark ? "#7c3aed" : "#6d28d9"
                },
                fontWeight: 600,
                textTransform: "none",
                boxShadow: 1
              }}
            >
              {action.label}
            </Button>
          );
        }

        return (
          <Button
            key={`action-${i}`}
            size="small"
            variant="contained"
            onClick={() => {}}
            sx={{
              minWidth: 70,
              height: 28,
              px: 1.5,
              py: 0.5,
              fontSize: "0.75rem",
              backgroundColor: isDark ? "#3b82f6" : "#2563eb",
              "&:hover": {
                backgroundColor: isDark ? "#2563eb" : "#1d4ed8"
              },
              fontWeight: 600,
              textTransform: "none",
              boxShadow: 1
            }}
          >
            {action.label}
          </Button>
        );
      })}
    </Box>
  );
}

function DataRow({
  cells,
  headers,
  columnWidths,
  columnMinWidths,
  rankColIndex,
  percentColIndex,
  actionsColIndex,
  position,
  rankValue,
  percentColor,
  stripLabel,
  hasChildren,
  isExpanded,
  onToggleExpand,
  isChild,
  theme,
  isDark
}) {
  return (
    <TableRow
      sx={{
        "&:hover": {
          backgroundColor: isDark
            ? "rgba(255,255,255,0.05)"
            : "rgba(0,0,0,0.04)"
        }
      }}
    >
      {headers.map((header, colIndex) => {
        const value = cells[colIndex];
        const width = `${columnWidths[colIndex]}%`;
        const minWidth = columnMinWidths[colIndex];

        if (colIndex === rankColIndex) {
          return (
            <TableCell
              key={colIndex}
              sx={{
                py: 1.5,
                position: "relative",
                width,
                minWidth
              }}
            >
              {!isChild && (
                <>
                  {percentColIndex !== -1 && (
                    <Box
                      sx={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: 3,
                        backgroundColor: percentColor,
                        borderRadius: "0 4px 4px 0",
                        overflow: "hidden",
                        transition: "width 0.25s ease",
                        display: "flex",
                        alignItems: "center",
                        zIndex: 2,
                        cursor: "default",
                        "&:hover": {
                          width: 160
                        },
                        "& .strip-label": {
                          opacity: 0,
                          transition: "opacity 0.1s ease"
                        },
                        "&:hover .strip-label": {
                          opacity: 1,
                          transition: "opacity 0.15s ease 0.1s"
                        }
                      }}
                    >
                      <Box
                        className="strip-label"
                        sx={{
                          pl: 1.5,
                          pr: 1,
                          fontSize: "0.6rem",
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: "0.07em",
                          color: "rgba(255,255,255,0.95)",
                          whiteSpace: "nowrap",
                          lineHeight: 1.3
                        }}
                      >
                        {stripLabel}
                      </Box>
                    </Box>
                  )}
                  <RankCell
                    header={header}
                    value={rankValue}
                    position={position}
                    isDark={isDark}
                  />
                </>
              )}
            </TableCell>
          );
        }

        if (typeIs(header, "iconobject")) {
          return (
            <TableCell key={colIndex} sx={{ py: 1.5, width, minWidth }}>
              <Box sx={{ pl: isChild ? 4 : 0 }}>
                <IconObjectCell value={value} theme={theme} />
              </Box>
            </TableCell>
          );
        }

        if (typeIs(header, "wld")) {
          return (
            <TableCell
              key={colIndex}
              align="right"
              sx={{ py: 1.5, width, minWidth }}
            >
              <WldCell value={value} isDark={isDark} />
            </TableCell>
          );
        }

        if (colIndex === percentColIndex) {
          return (
            <TableCell
              key={colIndex}
              align="right"
              sx={{ py: 1.5, width, minWidth }}
            >
              <PercentageCell
                value={value}
                color={percentColor}
                theme={theme}
              />
            </TableCell>
          );
        }

        if (colIndex === actionsColIndex) {
          return (
            <TableCell
              key={colIndex}
              align="center"
              sx={{ py: 1.5, width, minWidth }}
            >
              {!isChild && (
                <ActionsCell
                  actions={value}
                  hasChildren={hasChildren}
                  isExpanded={isExpanded}
                  onToggleExpand={onToggleExpand}
                  isDark={isDark}
                />
              )}
            </TableCell>
          );
        }

        return (
          <TableCell
            key={colIndex}
            align="right"
            sx={{ py: 1.5, width, minWidth }}
          >
            {value !== undefined && value !== null ? (
              <StatBox
                theme={theme}
                sx={{ color: theme.palette.text.secondary }}
              >
                {value}
              </StatBox>
            ) : null}
          </TableCell>
        );
      })}
    </TableRow>
  );
}

export default function DashboardStatsTable({ label, data }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [expandedRows, setExpandedRows] = useState({});

  const headers = data?.headers || [];
  const rows = data?.rows || [];

  const rankColIndex = headers.findIndex(isRankHeader);
  const percentColIndex = headers.findIndex(isPercentageHeader);
  const actionsColIndex = headers.findIndex((h) => typeIs(h, "actions"));

  const toggleRow = (rowKey) => {
    setExpandedRows((prev) => ({ ...prev, [rowKey]: !prev[rowKey] }));
  };

  if (!headers.length || !rows.length) return null;

  const RANK_WIDTH = 8;
  const ICONOBJECT_WIDTH = 28;
  const WLD_WIDTH = 16;
  const PERCENT_WIDTH = 14;
  const ACTIONS_WIDTH = 18;

  const RANK_MIN_WIDTH = 56;
  const ICONOBJECT_MIN_WIDTH = 180;
  const WLD_MIN_WIDTH = 130;
  const PERCENT_MIN_WIDTH = 110;
  const ACTIONS_MIN_WIDTH = 170;
  const PLAIN_MIN_WIDTH = 90;

  const plainColIndexes = headers
    .map((h, i) => i)
    .filter(
      (i) =>
        i !== rankColIndex &&
        i !== percentColIndex &&
        !typeIs(headers[i], "iconobject") &&
        !typeIs(headers[i], "wld") &&
        !typeIs(headers[i], "actions")
    );

  const consumedWidth =
    (rankColIndex !== -1 ? RANK_WIDTH : 0) +
    (percentColIndex !== -1 ? PERCENT_WIDTH : 0) +
    headers.filter((h) => typeIs(h, "iconobject")).length * ICONOBJECT_WIDTH +
    headers.filter((h) => typeIs(h, "wld")).length * WLD_WIDTH +
    headers.filter((h) => typeIs(h, "actions")).length * ACTIONS_WIDTH;

  const plainWidth =
    plainColIndexes.length > 0
      ? Math.max(8, (100 - consumedWidth) / plainColIndexes.length)
      : 0;

  const columnWidths = headers.map((header, i) => {
    if (i === rankColIndex) return RANK_WIDTH;
    if (i === percentColIndex) return PERCENT_WIDTH;
    if (typeIs(header, "iconobject")) return ICONOBJECT_WIDTH;
    if (typeIs(header, "wld")) return WLD_WIDTH;
    if (typeIs(header, "actions")) return ACTIONS_WIDTH;
    return plainWidth;
  });

  const columnMinWidths = headers.map((header, i) => {
    if (i === rankColIndex) return RANK_MIN_WIDTH;
    if (i === percentColIndex) return PERCENT_MIN_WIDTH;
    if (typeIs(header, "iconobject")) return ICONOBJECT_MIN_WIDTH;
    if (typeIs(header, "wld")) return WLD_MIN_WIDTH;
    if (typeIs(header, "actions")) return ACTIONS_MIN_WIDTH;
    return PLAIN_MIN_WIDTH;
  });

  const tableMinWidth = columnMinWidths.reduce((sum, w) => sum + w, 0);

  const percentValues =
    percentColIndex !== -1
      ? rows
          .map((r) => r[percentColIndex])
          .filter((v) => v !== null && v !== undefined)
      : [];
  const avgPercent =
    percentValues.length > 0
      ? percentValues.reduce((sum, v) => sum + v, 0) / percentValues.length
      : null;

  let closestRowIndex = -1;
  if (avgPercent !== null) {
    let closestDiff = Infinity;
    rows.forEach((row, i) => {
      const value = row[percentColIndex];
      if (value === null || value === undefined) return;
      const diff = Math.abs(value - avgPercent);
      if (diff < closestDiff) {
        closestDiff = diff;
        closestRowIndex = i;
      }
    });
  }

  const getPercentColor = (value, rowIndex) => {
    if (value === null || value === undefined || avgPercent === null)
      return isDark ? "#9ca3af" : "#6b7280";
    if (rowIndex === closestRowIndex) return isDark ? "#9ca3af" : "#6b7280";
    if (value > avgPercent) return isDark ? "#4ade80" : "#16a34a";
    return isDark ? "#f87171" : "#dc2626";
  };

  const getChildPercentColor = (value) => {
    if (value === null || value === undefined || avgPercent === null)
      return isDark ? "#9ca3af" : "#6b7280";
    return value > avgPercent
      ? isDark
        ? "#4ade80"
        : "#16a34a"
      : isDark
        ? "#f87171"
        : "#dc2626";
  };

  const getStripLabel = (rowIndex, value) => {
    if (value === null || value === undefined || avgPercent === null) return "";
    if (rowIndex === closestRowIndex) return "Near meta average";
    if (value > avgPercent) return "Above meta average";
    return "Below meta average";
  };

  const hasUnrankedRows =
    rankColIndex !== -1 &&
    rows.some((row) => {
      const value = row[rankColIndex];
      return value === null || value === undefined;
    });

  const headerCellSx = {
    fontWeight: 700,
    fontSize: "0.7rem",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: theme.palette.text.secondary,
    borderBottom: `2px solid ${theme.palette.divider}`
  };

  return (
    <Box>
      {label?.length > 0 && (
        <Typography
          variant="h5"
          sx={{
            mb: 3,
            color: "primary",
            fontWeight: 600,
            textAlign: "center",
            letterSpacing: { xs: "0.05em", sm: "0.1em" },
            textTransform: "uppercase",
            pt: 2,
          }}
        >
          {label}
        </Typography>
      )}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          backgroundColor: isDark
            ? "rgba(255,255,255,0.05)"
            : "rgba(0,0,0,0.02)",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
          boxShadow: isDark
            ? "0 2px 8px rgba(0,0,0,0.3)"
            : "0 2px 8px rgba(0,0,0,0.1)",
          borderRadius: 1
        }}
      >
        <Table sx={{ width: "100%", minWidth: tableMinWidth }}>
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: isDark
                  ? "rgba(255,255,255,0.03)"
                  : "rgba(0,0,0,0.02)"
              }}
            >
              {headers.map((header, i) => (
                <TableCell
                  key={i}
                  align={
                    typeIs(header, "iconobject")
                      ? "left"
                      : typeIs(header, "actions")
                        ? "center"
                        : i === rankColIndex
                          ? "center"
                          : "right"
                  }
                  sx={{
                    ...headerCellSx,
                    width: `${columnWidths[i]}%`,
                    minWidth: columnMinWidths[i]
                  }}
                >
                  {header.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, rowIndex) => {
              const rowKey = rowIndex;
              const actions =
                actionsColIndex !== -1 ? row[actionsColIndex] : null;
              const expandAction = Array.isArray(actions)
                ? actions.find((a) => a?.buttonType === "ExpandChildren")
                : null;
              const children = expandAction?.children || [];
              const hasChildren = children.length > 0;
              const rankValue =
                rankColIndex !== -1 ? row[rankColIndex] : undefined;
              const percentValue =
                percentColIndex !== -1 ? row[percentColIndex] : undefined;
              const percentColor = getPercentColor(percentValue, rowIndex);
              const stripLabel = getStripLabel(rowIndex, percentValue);

              return (
                <React.Fragment key={rowKey}>
                  <DataRow
                    cells={row}
                    headers={headers}
                    columnWidths={columnWidths}
                    columnMinWidths={columnMinWidths}
                    rankColIndex={rankColIndex}
                    percentColIndex={percentColIndex}
                    actionsColIndex={actionsColIndex}
                    position={rowIndex + 1}
                    rankValue={rankValue}
                    percentColor={percentColor}
                    stripLabel={stripLabel}
                    hasChildren={hasChildren}
                    isExpanded={!!expandedRows[rowKey]}
                    onToggleExpand={() => toggleRow(rowKey)}
                    isChild={false}
                    theme={theme}
                    isDark={isDark}
                  />

                  {hasChildren && (
                    <TableRow>
                      <TableCell
                        sx={{ py: 0, px: 0, border: 0 }}
                        colSpan={headers.length}
                      >
                        <Collapse
                          in={!!expandedRows[rowKey]}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box
                            sx={{
                              backgroundColor: isDark
                                ? "rgba(0,0,0,0.3)"
                                : "rgba(0,0,0,0.04)"
                            }}
                          >
                            <Table sx={{ width: "100%", minWidth: tableMinWidth }}>
                              <TableBody>
                                {children.map((childRow, childIndex) => (
                                  <DataRow
                                    key={childIndex}
                                    cells={childRow}
                                    headers={headers}
                                    columnWidths={columnWidths}
                                    columnMinWidths={columnMinWidths}
                                    rankColIndex={rankColIndex}
                                    percentColIndex={percentColIndex}
                                    actionsColIndex={actionsColIndex}
                                    position={childIndex + 1}
                                    rankValue={
                                      rankColIndex !== -1
                                        ? childRow[rankColIndex]
                                        : undefined
                                    }
                                    percentColor={getChildPercentColor(
                                      percentColIndex !== -1
                                        ? childRow[percentColIndex]
                                        : undefined
                                    )}
                                    stripLabel=""
                                    hasChildren={false}
                                    isExpanded={false}
                                    onToggleExpand={() => {}}
                                    isChild
                                    theme={theme}
                                    isDark={isDark}
                                  />
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
        {hasUnrankedRows && (
          <Box
            sx={{
              px: 2,
              py: 1.25,
              borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`,
              backgroundColor: isDark
                ? "rgba(255,255,255,0.02)"
                : "rgba(0,0,0,0.01)"
            }}
          >
            <Typography
              sx={{
                fontSize: "0.72rem",
                color: theme.palette.text.secondary,
                fontStyle: "italic",
                opacity: 0.7
              }}
            >
              * Ranking withheld due to limited game data — statistical accuracy
              may be reduced.
            </Typography>
          </Box>
        )}
      </TableContainer>
    </Box>
  );
}
